import React, { useState } from 'react';
import { VscSettings } from 'react-icons/vsc';
import Tag from './ui/atoms/Tag'; // Regular tags stay the same
import Chip from './ui/atoms/Chip'; // Import the new Chip component

type Tag = {
  id: string;
  name: string;
};

type SearchBarWithChipsProps = {
  onIngredientsChange: (ingredients: string[]) => void;
  selectedTags: string[];
  onTagToggle: (tagId: string) => void;
  tags: { [category: string]: Tag[] }; // Tags grouped by category
  onCategoryToggle: (category: string) => void; // Handle category selection
  selectedCategory: string | null; // Selected category
  showFilters: boolean; // Show filters state
  toggleFilters: () => void; // Toggle filters visibility
  setSelectedTags: React.Dispatch<React.SetStateAction<string[]>>; // Include this prop here
  chips: string[]; // Chips state passed from Search.tsx
  setChips: React.Dispatch<React.SetStateAction<string[]>>; // Set chips function passed from Search.tsx
};

const SearchBarWithChips = ({
  onIngredientsChange,
  selectedTags,
  onTagToggle,
  tags,
  onCategoryToggle,
  selectedCategory,
  showFilters,
  toggleFilters,
  setSelectedTags,
  chips, // Use chips from props
  setChips, // Use setChips from props
}: SearchBarWithChipsProps) => {
  const [input, setInput] = useState('');
  // only enable “Add” when there’s something to add
  const isAddEnabled = Boolean(input.trim());
  // determine what label to show on the “add” key
  // const returnKey = React.useMemo(() => {
  //   const ua = navigator.userAgent;
  //   if (/iPhone|iPad|iPod/.test(ua)) return 'Return';
  //   if (/Android/.test(ua)) return '↵';
  //   return 'Enter';
  // }, []);

  const returnKey = 'Enter';

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault();
      const newTerm = input.trim().toLowerCase();
      if (!chips.includes(newTerm)) {
        const updated = [...chips, newTerm];
        setChips(updated); // Update chips state in Search.tsx
        onIngredientsChange(updated);
      }
      setInput('');
    }
  };

  // extract adding logic so we can call it from both enter and click
  const handleAdd = () => {
    const term = input.trim().toLowerCase();
    if (!term) return;
    if (!chips.includes(term)) {
      const updated = [...chips, term];
      setChips(updated);
      onIngredientsChange(updated);
    }
    setInput('');
  };

  const handleDelete = (chipToDelete: string) => {
    const updated = chips.filter((chip) => chip !== chipToDelete);
    setChips(updated); // Update chips state in Search.tsx
    onIngredientsChange(updated);
  };

  const formatTagName = (name: string) => {
    return name
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
  };

  const handleClearAll = () => {
    // clear visual chips
    setChips([]);
    setSelectedTags([]);
    // **also** reset actual search terms
    onIngredientsChange([]);
  };

  return (
    <div className="mb-0">
      <div className="flex items-center justify-between">
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Search recipes or ingredients"
            id="search-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-sm"
          />
          {/* “Add” button that mirrors Enter, only active when there’s input */}
          <button
            onClick={handleAdd}
            disabled={!isAddEnabled}
            className={`
              absolute right-3 top-1/2 transform -translate-y-1/2
              px-2 py-1 border rounded text-xs
              transition
              ${isAddEnabled
                ? 'border-primary text-primary hover:bg-primary/10'
                : 'border-gray-200 text-gray-300 cursor-not-allowed'}
            `}
            aria-label="Add ingredient"
          >
            {returnKey}
          </button>
        </div>
        {/* Filter Icon */}
        <div
          className="filter-icon cursor-pointer"
          onClick={toggleFilters}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0px 10px',
          }}
        >
          <VscSettings
            size={24}
            className={`transition-all ${showFilters ? 'bg-primary text-white rounded-full p-1' : ''}`}
          />
        </div>
      </div>

      {/* Ingredients chips always show */}
      <div className="flex flex-wrap gap-2 mt-0 mb-1 items-center pb-1">
        {/* Clear all button moved to the left of chips */}
        {(chips.length > 0 || selectedTags.length > 0) && (
          <button
            onClick={handleClearAll}
            className="text-xs text-gray-500 underline mr-1 mt-1"
          >
            Clear all
          </button>
        )}

        {/* Chips for ingredients (replacing Tag with Chip) */}
        {chips.map((chip) => (
          <Chip
            key={chip}
            label={chip}
            removable
            onRemove={() => handleDelete(chip)}
            size="sm"
          />
        ))}

        {/* Chips for selected tags (replacing Tag with Chip) */}
        {selectedTags.map((tagId) => {
          const tagObj = Object.values(tags)
            .flat()
            .find((t) => t.id === tagId);
          if (!tagObj) return null;
          return (
            <Chip
              key={tagId}
              label={formatTagName(tagObj.name)}
              removable
              onRemove={() => onTagToggle(tagId)}
              size="sm"
            />
          );
        })}
      </div>
    </div>
  );
};

export default SearchBarWithChips;
